import React, {PropsWithChildren, useState} from 'react';
import {API, SDKApiKeyResponse} from '@chekin/api';
import {
  AUTH_INIT_MODELS,
  LocalStorage,
  type OldShortLink,
  QUERY_CACHE_KEYS,
  RESERVATION_SEARCH_MODE,
  SHORT_LINK_INSTANCE,
  SHORT_LINK_TYPE,
  type ShortLink,
} from '@chekin/core';
import i18n from 'i18next';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import ChekinProSettings from '../../ChekinProSettings';
import {useRedirectToUrlFromShortLink} from 'hooks/useRedirectToUrlFromShortLink';
import {environments} from 'configs';
import {
  HOST_ID_KEY,
  HOUSING_ID_KEY,
  MAIN_TOKEN_ID_KEY,
  RESERVATION_ID_KEY,
} from 'utils/constants';
import {
  getObjectDetails,
  getReservationFromStorage,
  getResourceType,
  getTemplateId,
  isNewShortLink,
  isShortLink,
} from './utils';

const IS_SDK_MODE = environments.VITE_APP_IS_SDK;

type ContextProps = {
  isTokenValid: boolean;
  isAuthInitialization: boolean;
  isAuthInitializationError: boolean;
  authError?: string;
  isSuccessSDK: boolean;
  foundReservationId?: string;
  mainToken?: string | null;
  hasToken?: boolean;
  objectId?: string;
  modelName?: string;
  link?: string;
  redirectTo?: string;
  linkType?: SHORT_LINK_TYPE;
  templateId?: string | null;
  reservationSearchMode?: RESERVATION_SEARCH_MODE;
};

const ChekinSDKContext = React.createContext<ContextProps>({} as ContextProps);

function ChekinSDKProvider({children}: PropsWithChildren) {
  const {t} = useTranslation();
  const [foundReservationId, setFoundReservationId] = useState<string | undefined>(
    getReservationFromStorage,
  );

  // chekin-sdk =-------------------------------------------------------------------
  const defaultLanguage = ChekinProSettings.defaultLanguage;

  React.useEffect(() => {
    if (IS_SDK_MODE) {
      void i18n.changeLanguage(defaultLanguage);
    }
  }, [defaultLanguage]);
  // chekin-sdk =-------------------------------------------------------------------

  const url = new URL(window.location.href);
  const pathSegments = url.pathname.split('/').filter(Boolean);
  const tokenFromUrl = pathSegments[0] || '';
  const shortToken = tokenFromUrl !== 'oauth' ? tokenFromUrl : '';

  const initParams = React.useMemo(
    () => ({
      apikey: ChekinProSettings.apiKey || import.meta.env.VITE_APP_TEST_API_KEY || '',
      resourceId: ChekinProSettings.reservationId || ChekinProSettings.housingId,
      externalResourceId:
        ChekinProSettings.externalReservationId || ChekinProSettings.externalHousingId,
      resourceType: getResourceType(),
      encryptionEnabled: IS_SDK_MODE,
    }),
    [],
  );

  const {
    data,
    error,
    isLoading: isAuthInitialization,
    isLoadingError: isAuthInitializationError,
    isSuccess: isSuccessSDK,
  } = useQuery<(OldShortLink | ShortLink) | SDKApiKeyResponse, Error>({
    queryKey: [QUERY_CACHE_KEYS.apiClient, shortToken, initParams],
    queryFn: () => (IS_SDK_MODE ? API.initSDK(initParams) : API.init(shortToken)),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: IS_SDK_MODE ? Boolean(initParams.apikey) : Boolean(shortToken),
    refetchOnReconnect: false,
  });

  const isTokenValid = Boolean(data);
  const templateId = getTemplateId(data);

  React.useEffect(() => {
    if (isShortLink(data)) {
      let reservationId;
      let housingId;
      let userId;
      if (isNewShortLink(data)) {
        if (data.instance_class === SHORT_LINK_INSTANCE.reservation) {
          reservationId = data?.instance_id;
        }
        if (data.instance_class === SHORT_LINK_INSTANCE.housing) {
          housingId = data?.instance_id;
        }
      } else {
        if (data?.model_name === AUTH_INIT_MODELS.reservation) {
          reservationId = data?.object_id;
        }
        if (data?.model_name === AUTH_INIT_MODELS.housing) {
          housingId = LocalStorage.get(HOUSING_ID_KEY) || data?.object_id;
        }
        if (data?.model_name === AUTH_INIT_MODELS.user) {
          userId = LocalStorage.get(HOST_ID_KEY) || data?.object_id;
        }
      }

      setFoundReservationId(reservationId);
      LocalStorage.set(RESERVATION_ID_KEY, reservationId);
      LocalStorage.set(HOUSING_ID_KEY, housingId);
      LocalStorage.set(HOST_ID_KEY, userId);
    }
  }, [data]);

  const linkType = React.useMemo(() => {
    if (!isShortLink(data)) return undefined;

    if (data?.type) return data.type;

    const modelName = getObjectDetails(data).model;
    if (modelName === AUTH_INIT_MODELS.housing || modelName === AUTH_INIT_MODELS.user) {
      return SHORT_LINK_TYPE.housingCheckinOnline;
    }

    return undefined;
  }, [data]);

  useRedirectToUrlFromShortLink({
    linkType,
    token: shortToken,
  });

  const authError = React.useMemo<string | undefined>(() => {
    if (!isAuthInitializationError) return;
    if (IS_SDK_MODE) {
      if (!error) {
        return t('dns_is_forbidden_error_message');
      }
      return error?.message;
    }
  }, [error, isAuthInitializationError, t]);

  const mainToken = React.useMemo(() => {
    if (!IS_SDK_MODE && shortToken) {
      const previousToken = LocalStorage.get(MAIN_TOKEN_ID_KEY);
      if (previousToken && previousToken !== shortToken) {
        LocalStorage.remove(RESERVATION_ID_KEY);
      }
      LocalStorage.set(MAIN_TOKEN_ID_KEY, shortToken);
    }

    return shortToken || LocalStorage.get(MAIN_TOKEN_ID_KEY);
  }, [shortToken]);

  const contextValue = React.useMemo<ContextProps>(
    () => ({
      isTokenValid,
      isAuthInitialization,
      isAuthInitializationError,
      authError,
      foundReservationId,
      mainToken,
      isSuccessSDK,
      objectId: getObjectDetails(data).id,
      modelName: getObjectDetails(data).model,
      link: getObjectDetails(data).link,
      redirectTo: getObjectDetails(data).redirectTo,
      reservationSearchMode: (getObjectDetails(data).searchMode ||
        RESERVATION_SEARCH_MODE.both) as RESERVATION_SEARCH_MODE,
      linkType,
      hasToken: Boolean(shortToken),
      templateId,
    }),
    [
      linkType,
      authError,
      isAuthInitialization,
      isAuthInitializationError,
      isSuccessSDK,
      isTokenValid,
      mainToken,
      foundReservationId,
      data,
      shortToken,
      templateId,
    ],
  );

  return <ChekinSDKContext.Provider value={contextValue} children={children} />;
}

export {ChekinSDKContext, ChekinSDKProvider};
